import { AttachingMethod, Attachment } from '../../../../types';
import { experiments } from '../../../experiments';
import { logger } from '../../../util/logger';
import { renderModel } from '../../../util/renderModel';
import { httpClient } from '../../httpClient';
import { getMediaAuthToken } from '../getMediaToken';
import { SupportedFileType } from './constants';
import { getMediaType } from './getMediaType';
import { getSupportedFileType } from './getSupportedFileType';
import { getUploadUrl } from './getUploadUrl';
import { mapUploadResponse } from './mapUploadResponse';
import { UploadFileResponse } from './types';
import { DynamicsMediaUploader } from './uploadDynamicsMedia';
import { WixmpMediaUploader } from './uploadWixmpMedia';

const createBody = (file: File, uploadToken: string): FormData => {
  const formData = new FormData();
  const mediaType = getMediaType(file);

  formData.set('upload_token', uploadToken);
  formData.set('media_type', mediaType);
  formData.set('parent_folder_id', 'visitor-uploads');
  formData.set('file', file, file.name);

  return formData;
};

// Images route to WixMP only when not in live chat and not using the Dynamics attach path.
// In all other cases (live chat, attachFile, or non-image files) everything goes to Dynamics.
export const uploadMedia = async (
  files: File[],
  attachingMethod: AttachingMethod,
  signal: AbortSignal,
  attachFile: boolean,
  isLiveChat: boolean,
): Promise<Attachment[]> => {
  if (attachFile || isLiveChat) {
    return uploadViaDynamicsOrLegacy(files, attachingMethod, signal);
  }
  // getSupportedFileType classifies GIFs separately (they have their own size cap), but they are
  // still images and are explicitly supported by the AI gateway (SUPPORTED_AI_GATEWAY_IMAGE_FILES).
  const imageFiles = files.filter((f) => {
    const type = getSupportedFileType(f);
    return type === SupportedFileType.Image || type === SupportedFileType.Gif;
  });
  if (imageFiles.length !== files.length) {
    throw new Error('[uploadMedia] Only image files are supported on the WixMP (chatbot) path');
  }
  return WixmpMediaUploader.upload(imageFiles, attachingMethod, signal);
};

const uploadViaDynamicsOrLegacy = (
  files: File[],
  attachingMethod: AttachingMethod,
  signal: AbortSignal,
): Promise<Attachment[]> =>
  experiments.is('specs.chatbot.DynamicsMigration', 'true')
    ? DynamicsMediaUploader.upload(files, attachingMethod, signal)
    : uploadMediaLegacy(files, attachingMethod, signal);

export const uploadMediaLegacy = async (
  files: File[],
  attachingMethod: AttachingMethod,
  signal: AbortSignal,
): Promise<Attachment[]> => {
  const mediaAuthToken = await getMediaAuthToken();

  try {
    return Promise.all(
      files.map(async (file) => {
        const { upload_url, upload_token } = await getUploadUrl(file, mediaAuthToken);
        const body = createBody(file, upload_token);
        const source = httpClient.getInstance().CancelToken.source();
        signal.onabort = () => source.cancel();

        const {
          data: [uploadedFile],
        } = await httpClient
          .getInstance()
          .post<UploadFileResponse[]>(upload_url, body, { cancelToken: source.token });

        return mapUploadResponse(uploadedFile, attachingMethod);
      }),
    );
  } catch (error) {
    logger.error({
      message: 'fail to upload media',
      error,
      callParams: renderModel.get(),
    });
    throw error;
  }
};
